import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Sparkles, TrendingUp, Award, Users, Trophy, Shield, Download, Star, CheckCircle, CheckCircle2, Apple, Smartphone, Gamepad2, Puzzle, Languages, Headphones, BookOpen, BarChart, Notebook, Smile, TabletSmartphone, Clock, Globe, MonitorPlay, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { features, ageGroups } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SubscriptionPopup from '../components/SubscriptionPopup';
import { useSubscriptionPopup } from '../hooks/useSubscriptionPopup';

const iconMap = {
  Sparkles,
  TrendingUp,
  Award,
  Users,
  Trophy,
  Shield,
  Gamepad2,
  Puzzle,
  Languages,
  Headphones,
  BookOpen,
  BarChart,
  Notebook,
  Smile,
  TabletSmartphone,
  Clock,
  Globe,
  MonitorPlay
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const Home = () => {
  const [selectedRegion, setSelectedRegion] = useState('GLOBAL');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);

  // Subscription popup management
  const { isOpen: isPopupOpen, openPopup, closePopup } = useSubscriptionPopup({
    autoShowDelay: 15000, // Show after 15 seconds
    enableExitIntent: true
  });

  useEffect(() => {
    // try to detect user locale and default to India if likely
    try {
      const lang = (navigator && (navigator.language || navigator.userLanguage)) || '';
      if (/\b(in|hi|en-IN)\b/i.test(lang)) {
        setSelectedRegion('IN');
      } else {
        setSelectedRegion('GLOBAL');
      }
    } catch (e) {
      // ignore and keep default
    }
  }, []);

  // Fetch pricing plans from API for India
  useEffect(() => {
    const fetchPlans = async () => {
      if (selectedRegion !== 'IN') {
        // For Global, use empty array or fetch from another endpoint
        setPricingPlans([]);
        return;
      }

      setPlansLoading(true);
      setPlansError(null);

      try {
        const response = await fetch('https://admin.goodikids.com/api/v1/plans');

        if (!response.ok) {
          throw new Error('Failed to fetch plans');
        }

        const data = await response.json();

        // Transform API data to match our component structure
        if (data.status === 'success' && data.data) {
          const transformedPlans = data.data.map(plan => ({
            id: plan.plan_id || plan.id,
            region: 'IN',
            currency: 'INR',
            name: plan.name,
            price: parseFloat(plan.price),
            period: plan.duration_label || plan.duration,
            subtitle: plan.subtitle || `Full access • ${plan.duration_label || plan.duration}`,
            description: plan.description || '',
            features: plan.features || [],
            highlighted: plan.is_popular === 1 || plan.highlighted === true
          }));
          setPricingPlans(transformedPlans);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlansError(error.message);
        // Set empty array on error
        setPricingPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [selectedRegion]);

  const formatPrice = (plan) => {
    try {
      if (plan.currency === 'INR') {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(plan.price);
      }
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(plan.price);
    } catch (e) {
      // fallback
      return plan.currency === 'INR' ? `₹${plan.price}` : `$${Number(plan.price).toFixed(2)}`;
    }
  };

  const displayedPlans = pricingPlans;

  // simple carousel images (public/ folder)
  const carouselImages = [
    '/banner/1 (1).jpeg',
    '/banner/1 (2).jpeg',
    '/banner/1 (3).jpeg',
    '/banner/1 (4).jpeg',
    '/banner/1 (5).jpeg',
    '/banner/1 (6).jpeg',
    '/banner/1 (7).jpeg',
    '/banner/1 (8).jpeg'
  ];
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = () => setCurrentSlide((s) => (s + 1) % carouselImages.length);
  const prevSlide = () => setCurrentSlide((s) => (s - 1 + carouselImages.length) % carouselImages.length);

  useEffect(() => {
    const t = setInterval(nextSlide, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen">
      <Header openPopup={openPopup} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50 -z-10"></div>
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 150 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm mb-6">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-medium text-gray-700">Trusted by 10,000+ families</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">

                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                  Fun learning that builds values and character for life.
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Turning every screen moment into a step toward kindness, confidence, and lifelong values that build good character and develop personality.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={openPopup} className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white rounded-full px-8 py-6 text-lg">
                  <Apple className="w-5 h-5 mr-2" />
                  Download for iOS
                </Button>
                <Button asChild variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-50 rounded-full px-8 py-6 text-lg">
                  <a href="https://play.google.com/store/apps/details?id=com.goodikids&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <Smartphone className="w-5 h-5 mr-2" />
                    Download for Android
                  </a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...springConfig, delay: 0.4 }}
              className="relative"
            >
              <div className="relative">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ ...springConfig, duration: 0.4 }}
                  className="rounded-3xl shadow-2xl overflow-hidden"
                >
                  <img
                    src={carouselImages[currentSlide]}
                    alt={`Goodikids slide ${currentSlide + 1}`}
                    className="w-full h-auto block"
                  />
                </motion.div>

                {/* controls */}
                <button
                  onClick={prevSlide}
                  aria-label="Previous"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow-md hover:bg-white"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={nextSlide}
                  aria-label="Next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow-md hover:bg-white"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>

                {/* indicators */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-3 flex items-center gap-2">
                  {carouselImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      aria-label={`Go to slide ${idx + 1}`}
                      className={`w-2 h-2 rounded-full ${idx === currentSlide ? 'bg-orange-500' : 'bg-white/70'} shadow`}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">10000+ Questions</p>
                    <p className="text-xs text-gray-500">Across all subjects</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Overview Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-fuchsia-50 via-pink-50 to-rose-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-8 md:p-12 shadow-sm border border-orange-100">
              <h3 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                  About Goodikids
                </span>
              </h3>
              <p className="text-2xl font-medium text-orange-600 max-w-3xl mx-auto mb-8 italic">
                “Where character and personality grow with every tap.”
              </p>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed">
                Goodikids is a joyful digital world that nurtures the heart, mind, and behavior of every child.
                It helps children learn values, manners, ethics, and life skills through story-based quizzes, guided reflections, emotional summaries, images and fun rewards.
              </p>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed">
                Every moment on Goodikids turns screen time into meaningful learning filled with kindness, empathy, honesty, and gratitude.
              </p>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Safe, ad-free, and beautifully designed, Goodikids isn’t just a learning app — it’s a movement to raise confident, compassionate, and well-mannered children in today’s digital world.
              </p>
            </div>
          </motion.div>

          {/* <div className="grid md:grid-cols-4 gap-8">
            {ageGroups.map((group, index) => (
              <motion.div
                key={group.range}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full border-2 hover:border-orange-300 transition-all hover:shadow-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{group.range}</h4>
                  <p className="text-sm font-semibold text-orange-500 mb-2">{group.title}</p>
                  <p className="text-sm text-gray-600">{group.description}</p>
                </Card>
              </motion.div>
            ))}
          </div> */}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Main Features</span>
            </h3>
            <p className="text-xl text-orange-600 max-w-2xl mx-auto">Everything your child needs for an amazing learning experience</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              // fallback to Sparkles if the icon name from mock.js isn't present in iconMap
              const IconComponent = iconMap[feature.icon] ?? Sparkles;
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className="p-8 h-full bg-white/80 backdrop-blur-sm border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-700 font-medium leading-relaxed">{feature.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Why Goodikids Is Needed */}
      <section className="py-20 px-6 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-10"
          >
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Why Goodikids Is Needed</span>
            </h3>
            <p className="text-xl text-orange-600 max-w-3xl mx-auto mb-6">Because the World Is Changing — and So Must Learning.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { icon: "💬", title: "Restoring Values", desc: "Revives empathy, kindness, and manners fading in the digital era." },
              { icon: "🧠", title: "Emotional Strength", desc: "Builds calmness, gratitude, and resilience alongside academics." },
              { icon: "📱", title: "Purposeful Screen Time", desc: "Converts entertainment into character-building engagement." },
              { icon: "👨‍👩‍👧", title: "Parent Support", desc: "Helps families nurture values effortlessly in daily life." },
              { icon: "🏫", title: "School Integration", desc: "Provides ready-to-use, structured moral education tools." }
            ].map((item, idx) => (
              <Card key={idx} className="p-6 bg-white border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h4 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">{item.title}</h4>
                <p className="text-gray-700 font-medium">{item.desc}</p>
              </Card>
            ))}

            <Card className="p-6 bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 shadow-lg flex items-center justify-center text-center">
              <p className="text-lg font-bold text-gray-800">Goodikids bridges learning and living — helping children understand not just what’s right, but why it’s right.</p>
            </Card>
          </motion.div>
        </div>
      </section>



      {/* Why Parents & Schools Trust Goodikids */}
      <section className="py-16 px-6 bg-gradient-to-br from-white to-orange-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-10"
          >
            <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">💖 Why Parents & Schools Trust Goodikids</span>
            </h3>
            <p className="text-lg text-orange-600 max-w-3xl mx-auto">Built for Families, Perfect for Classrooms, Loved by Children.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.05 }}
              className="space-y-6"
            >
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">For Parents</h4>
                <ul className="text-gray-700 font-medium space-y-2">
                  <li>Track your child’s growth through weekly reports and certificates.</li>
                  <li>Encourage positive screen habits and shared reflection.</li>
                  <li>Reinforce family values through guided daily learning.</li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">For Schools</h4>
                <ul className="text-gray-700 font-medium space-y-2">
                  <li>Ready-to-use, age-wise modules for moral and life-skills education.</li>
                  <li>Fun, structured lessons that simplify teaching values.</li>
                  <li>Printable reports and class progress tracking tools.</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.12 }}
              className="space-y-6"
            >
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">For Global Learners</h4>
                <p className="text-gray-700 font-medium">Available in English, Hindi, and regional languages. Teaches universal values that connect across cultures.</p>
              </div>

              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Child-Safe and Trustworthy</h4>
                <ul className="text-gray-700 font-medium space-y-2">
                  <li>100% ad-free, no chats, no distractions — only goodness.</li>
                  <li>Gentle visuals, calming sound, and complete child safety.</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How Goodikids Helps Children Grow */}
      <section className="py-16 px-6 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-8"
          >
            <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🌟 How Goodikids Helps Children Grow</span>
            </h3>
            <p className="text-lg text-orange-600 max-w-3xl mx-auto">From Good Lessons to Good Habits — Step by Step.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.05 }}
            className="grid md:grid-cols-2 gap-8"
          >
            <div className="space-y-4">
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🧩 Values in Action</h4>
                <p className="text-gray-700 font-medium">Teaches honesty, kindness, patience, teamwork, and gratitude.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🎮 Learning Through Play</h4>
                <p className="text-gray-700 font-medium">Fun quizzes, stories, and badges make learning enjoyable.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🧘 Emotional Growth</h4>
                <p className="text-gray-700 font-medium">Builds calm thinking, patience, and positive responses.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">💬 Communication Skills</h4>
                <p className="text-gray-700 font-medium">Promotes polite speech, greetings, and peaceful conflict solving.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🪴 Responsibility</h4>
                <p className="text-gray-700 font-medium">Inspires children to stay organized, help others, and care for surroundings.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🧠 Moral Reasoning</h4>
                <p className="text-gray-700 font-medium">Helps children think before acting through real-life ethical choices.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">🏆 Confidence Building</h4>
                <p className="text-gray-700 font-medium">Every reward and certificate reinforces self-belief.</p>
              </div>
              <div className="p-6 bg-white rounded-2xl border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                <h4 className="font-bold text-lg mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">👥 Social Awareness</h4>
                <p className="text-gray-700 font-medium">Encourages inclusion, empathy, and understanding of differences.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.12 }}
            className="mt-8 text-center"
          >
            <p className="font-semibold text-gray-800">🌈 Whole-Child Growth — Balances academics with emotional and social intelligence.</p>
            <p className="mt-3 text-gray-700">🚀 Lifelong Impact — Shapes future leaders who act with courage, compassion, and conscience.</p>
          </motion.div>
        </div>
      </section>

      {/* What Makes Goodikids Unique */}
      <section className="py-16 px-6 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 mb-10">✨ What Makes Goodikids Unique:</p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.06 }}
            className="bg-white rounded-2xl border-2 border-orange-200 shadow-xl p-8 md:p-10 text-left inline-block w-full max-w-3xl"
          >
            <ul className="space-y-6">
              {[
                "Teaches values through action, not lectures.",
                "Blends stories, emotions, and rewards for deeper impact.",
                "Works even when parents are busy — learning continues naturally at home."
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className="mt-1 bg-green-100 p-1.5 rounded-full shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-lg text-gray-700 font-medium leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50" id="pricing">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-6"
          >
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Choose Your Plan</span>
            </h3>
            <p className="text-xl text-orange-600 max-w-2xl mx-auto">Start free, upgrade anytime. No credit card required!</p>
          </motion.div>

          {/* region toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-full bg-white/60 p-1">
              <button
                onClick={() => setSelectedRegion('IN')}
                aria-pressed={selectedRegion === 'IN'}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedRegion === 'IN' ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow' : 'text-gray-700'}`}
              >
                India
              </button>
              <button
                onClick={() => setSelectedRegion('GLOBAL')}
                aria-pressed={selectedRegion === 'GLOBAL'}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedRegion === 'GLOBAL' ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow' : 'text-gray-700'}`}
              >
                Global
              </button>
            </div>
          </div>

          {/* Loading State */}
          {plansLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              <p className="mt-4 text-gray-600">Loading pricing plans...</p>
            </div>
          )}

          {/* Error State */}
          {plansError && !plansLoading && (
            <div className="text-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
                <p className="text-red-700 font-medium">Failed to load pricing plans</p>
                <p className="text-red-600 text-sm mt-2">Showing cached plans</p>
              </div>
            </div>
          )}

          {/* Plans Grid */}
          {!plansLoading && (
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {displayedPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className={`p-8 h-full relative ${plan.highlighted
                    ? 'border-4 border-orange-500 shadow-2xl scale-105'
                    : 'border-2 border-orange-200 hover:border-orange-500 shadow-lg hover:shadow-2xl'
                    } transition-all duration-300`}>
                    {plan.highlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </div>
                    )}
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">{formatPrice(plan)}</span>
                      <span className="text-gray-500">/{plan.period}</span>
                    </div>
                    {plan.description && (
                      <p className="text-gray-600 mb-4 text-sm">{plan.description}</p>
                    )}
                    <ul className="space-y-3 mb-8">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))
                      ) : (
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">Full access to all features</span>
                        </li>
                      )}
                    </ul>
                    <Button
                      onClick={openPopup}
                      className={`w-full rounded-full py-6 ${plan.highlighted
                        ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                    >
                      {plan.price === 0 ? 'Start Free' : 'Choose Plan'}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>




      {/* Download CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-500 via-yellow-500 to-green-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center text-white"
          >
            <Download className="w-16 h-16 mx-auto mb-6" />
            <h3 className="text-4xl md:text-5xl font-bold mb-6 text-white">Join the Movement</h3>
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.05 }}
              className="text-center italic text-lg border-l-4 border-orange-300 pl-6 mb-4"
            >
              “Let’s raise a generation that learns to think kindly, act wisely, and live responsibly.”
            </motion.blockquote>


            <p className="text-xl mb-8 opacity-90">Let’s Raise a Kinder, Stronger Generation. Download Goodikids today ! — and turn every screen moment into a step toward confidence, compassion, and lifelong character growth.!</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={openPopup} className="bg-white text-orange-500 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold">
                <Apple className="w-5 h-5 mr-2" />
                App Store
              </Button>
              <Button asChild className="bg-white text-orange-500 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold">
                <a href="https://play.google.com/store/apps/details?id=com.goodikids&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <Smartphone className="w-5 h-5 mr-2" />
                  Google Play
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Subscription Popup */}
      <SubscriptionPopup isOpen={isPopupOpen} onClose={closePopup} />
    </div>
  );
};

export default Home;
